import { SelectionModel } from '@angular/cdk/collections';
import { Component, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ToastrService } from 'ngx-toastr';
import { DateHelper } from 'src/shared/helpers/date.helper';
import { ISearchParams } from '../interfaces/search-params';
import { SearchResultItem } from '../interfaces/search-result-item';
import { Template } from '../interfaces/template';
import { CreateTemplateRequest } from '../models/create-template-request';
import { DataHelperService } from '../services/data-helper.service';
import { DataService } from '../services/data.service';
import { TemplateService } from '../services/template.service';
import { TemplateCreatorModalComponent } from '../template-creator-modal/template-creator-modal.component';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../material';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';


@Component({
    selector: 'app-report',
    templateUrl: './report.component.html',
    imports: [CommonModule,MaterialModule,ReactiveFormsModule,MatProgressSpinnerModule],
})
export class ReportComponent implements OnInit {
  constructor(public dialog: MatDialog,
              private templateService: TemplateService,
              private dataHelperService: DataHelperService,
              private formBuilder: UntypedFormBuilder,
              private dataService: DataService,
              private toastrService: ToastrService) { }
  public templates: Array<Template>;

  public readonly VantaDeviceModelCode: string = 'vanta';
  public displayedColumns: string[] = ['select', 'shotnumber', 'serialnumber', 'methoddisplayname', 'timestamp' ];
  public dataSource: MatTableDataSource<SearchResultItem>;
  public selection: SelectionModel<SearchResultItem>;
  public firstSearch = true;
  public searching = false;
  public devices: Array<any> = [];
  public methods: Array<string>;
  public serialNumbers: Array<string> = ['All'];
  public searchResult: Array<SearchResultItem>;
  public checkBoxSelectAll = false;

  // FORM
  public searchForm: UntypedFormGroup;
  public startDate: Date;
  public endDate: Date;
  public selectedMethod: string;
  public selectedSerialNumber: string;
  public selectedResultItem: SearchResultItem;

  public error: any = {isError: false, errorMessage: ''};
  public areTemplatesLoaded: boolean;

  public step = 0;

  public ngOnInit() {
    const startDate = new Date();
    const endDate = new Date();
    startDate.setDate(DateHelper.today().getDate() - 7);
    endDate.setDate(DateHelper.today().getDate());

    this.searchForm = this.formBuilder.group({
      startDate: [startDate, Validators.required],
      endDate: [endDate, Validators.required],
      selectedSerialNumber: ['All'],
      selectedMethod: ['All']
    });

    this.methods = [
      'All',
      'Hot Alloy Plus',
      'AlloyPlus',
      'LeadPaint',
      'RoHS Plus',
      'RoHS',
      'Coating',
      'Soil',
      'Geochem(2)',
      'Precious Metals',
      'AlloyPlus BigSet',
      'Car Catalyst',
      'IP 65 Oil',
      'GeoChem REE(2)'
    ];

    this.dataHelperService.getDevicesByCode(this.VantaDeviceModelCode)
      .subscribe(devices => {
        this.devices = devices;
        this.devices.forEach(device => {
          this.serialNumbers.push(device.serialNumber);
        });
    });

    this.getTemplates();
  }

  public openDesignTemplateDialog(): void {
    const dialogRef = this.dialog.open(TemplateCreatorModalComponent, {
      width: '700px',
      data: {
        vantaShotTemplate: require('./../../assets/json2csv/vantashot.template.json')
      },
      id: 'template-editor'
    });

    dialogRef.afterClosed().subscribe((createTemplateRequest: CreateTemplateRequest) => {
      if (createTemplateRequest !== undefined) {
        this.templateService.createTemplate(createTemplateRequest).subscribe(() => {
          this.areTemplatesLoaded = false;
          this.toastrService.success('New template was created!');
          this.getTemplates();
        }, (error) => {
          this.toastrService.error('There was an issue during creating of template.');
        });
      }
    });
  }

  public editTemplate(template: Template): void {
    const dialogRef = this.dialog.open(TemplateCreatorModalComponent, {
      width: '700px',
      data: {
        vantaShotTemplate: require('./../../assets/json2csv/vantashot.template.json'),
        jsonTemplate: template
      },
      id: 'template-editor'
    });

    dialogRef.afterClosed().subscribe((createTemplateRequest: CreateTemplateRequest) => {
      if (createTemplateRequest !== undefined) {
        this.templateService.updateTemplate(createTemplateRequest).subscribe(() => {
          this.areTemplatesLoaded = false;
          this.toastrService.success('New template was updated!');
          this.getTemplates();
        }, (error) => {
          this.toastrService.error('There was an issue during creating of template.');
        });
      }
    });
  }

  public deleteTemplate(template: Template): void {
    this.templateService.deleteTemplate(template.id).subscribe(() => {
      this.areTemplatesLoaded = false;
      this.toastrService.success('Template was deleted!');
      this.getTemplates();
    }, (error) => {
      this.toastrService.error('There was an issue during deleting of template.');
    });
  }

  public setStep(index: number): void {
    this.step = index;
  }

  public nextStep(): void {
    this.step++;
  }

  public prevStep(): void {
    this.step--;
  }

  public onSubmit(form: any): void {
    this.searchResult = [];
    const params: ISearchParams = {
      beginDate: form.startDate,
      endDate: form.endDate,
      pageNumber: 0,
      pageSize: 15,
      method: '',
      serialNumber: (form.selectedSerialNumber !== 'All' ? form.selectedSerialNumber : '')
    };

    this.firstSearch = false;
    this.searching = true;
    this.dataService.doSearchVantaShots(params)
      .subscribe((result: Array<SearchResultItem>) => {
        if ((result || []).length > 0) {
          result.forEach(item => { item.checked = false; });
          this.searchResult = result;
          this.dataSource = new MatTableDataSource<SearchResultItem>(this.searchResult);
          this.selection = new SelectionModel<SearchResultItem>(true, []);
          window.setTimeout(() => {
            document.getElementById('vantaSearchResults').scrollTop = 0;
          }, 10);
        } else {
          this.dataSource = new MatTableDataSource<SearchResultItem>([]);
          this.searchResult = [];
        }
        this.searching = false;
      });
    }

    public showData(result: SearchResultItem): void {
      this.selectedResultItem = result;
    }

    public validDateRange() {
      if (new Date(this.searchForm.controls.endDate.value) < new Date(this.searchForm.controls.startDate.value)) {
        this.error = { isError: true, errorMessage: `End Date can't before start date`};
      } else {
        this.error = { isError: false };
      }
    }

    public isDownloadButtonActive(): boolean {
      return this.selection.selected.length > 0;
    }

    public downloadFiles(): void {
      this.selection.selected.forEach((selected) => console.log(selected.id));
    }

    public isAllSelected() {
      if (this.selection === null || this.selection === undefined) {
        return false;
      }
      const numSelected = this.selection.selected.length;
      const numRows = this.dataSource.data.length;
      return numSelected === numRows;
    }

    public masterToggle() {
      this.isAllSelected() ?
        this.selection.clear() :
          this.dataSource.data.forEach(row => this.selection.select(row));
    }

    public checkboxLabel(row?: SearchResultItem): string {
      if (!row) {
        return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
      }
      return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.id}`;
    }

    private getTemplates(): void {
      this.templateService.getTemplates()
        .subscribe((templates) => {
          this.templates = templates;
          this.areTemplatesLoaded = true;
        });
    }
}
